import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Cloud, FolderOpen, Upload, Download, Trash2, Archive, RefreshCw,
  FileText, Image as ImageIcon, File as FileIcon, Loader2,
  Wand2, ExternalLink, CheckCircle2, XCircle, Pencil, ClipboardCheck,
  Scan, UploadCloud, Wrench,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { usePinSecurity } from "@/contexts/PinContext";
import SyncSignedButton from "./SyncSignedButton";
import { useDataRefresh } from "@/hooks/useDataRefresh";
import ManualCropDialog from "./ManualCropDialog";
import CropPreviewDialog from "./CropPreviewDialog";

// V1.24.3 · StudentFilesPanel
// Panel completo de archivos corporativos del alumno en OneDrive:
// - Lista por categoría con versión, tamaño, fecha y actor
// - Subida real por categoría (multipart)
// - Descarga desde OneDrive (stream binario proxy backend)
// - Archivar / eliminar (sudo)
// - Botón "Migrar documentos legacy" (base64 → OneDrive)

const CATEGORIES = [
  { key: "IDENTIDAD",  label: "Identidad",  hint: "DNI, foto, permiso de residencia" },
  { key: "CONTRATOS",  label: "Contratos",  hint: "Contrato, particulares, autorizaciones legales" },
  { key: "DGT",        label: "DGT",        hint: "Permisos, tasas, autoescuelas anteriores" },
  { key: "MEDICO",     label: "Médico",     hint: "CIR, psicotécnico, certificados sanitarios" },
  { key: "FINANCIERO", label: "Financiero", hint: "Facturas, recibos, mandatos SEPA, justificantes" },
  { key: "OTROS",      label: "Otros",      hint: "Documentos varios" },
];

const iconForExt = (ext) => {
  const e = (ext || "").toLowerCase();
  if (["pdf"].includes(e)) return FileText;
  if (["jpg", "jpeg", "png", "webp", "heic", "gif"].includes(e)) return ImageIcon;
  return FileIcon;
};

const fmtSize = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export default function StudentFilesPanel({ studentId }) {
  const pin = usePinSecurity();
  const [files, setFiles] = useState([]);
  const [templates, setTemplates] = useState([]);   // matriz documental (keys)
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [uploadingCat, setUploadingCat] = useState("");
  const [editing, setEditing] = useState(null);      // {file} para modal editar
  const [rejecting, setRejecting] = useState(null);  // {file} para modal rechazar
  // V1.42.6 · CROP + sustitución del original en OneDrive
  const [cropping, setCropping] = useState(null);    // {file} para ManualCropDialog
  const [replacingOd, setReplacingOd] = useState(""); // fileId en proceso
  const [previewingOd, setPreviewingOd] = useState(null); // {file} para CropPreviewDialog
  const fileInputs = useRef({});

  // V1.81 · Repara archivos nombrados .jpg que en realidad son PDF/PNG/HEIC
  const [fixing, setFixing] = useState(false);
  const doFixExtensions = async () => {
    setFixing(true);
    try {
      const { data } = await api.post(`/students/${studentId}/files/fix-extensions`);
      if (data.fixed) {
        toast.success(`${data.fixed} archivo(s) renombrado(s) con su extensión correcta`);
        load();
      } else {
        toast.info("Todos los archivos ya tienen la extensión correcta");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo reparar");
    } finally {
      setFixing(false);
    }
  };

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const [{ data: filesData }, { data: tplData }] = await Promise.all([
        api.get(`/students/${studentId}/files`),
        api.get("/document-templates").catch(() => ({ data: [] })),
      ]);
      setFiles(filesData?.files || []);
      setTemplates(tplData || []);
      // V1.85 · Avisa al panel ALTA DGT de que la documentación ha cambiado
      // (p. ej. psicotécnico subido después) para que recalcule su estado.
      window.dispatchEvent(new CustomEvent("student-files-changed", { detail: { studentId } }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error al cargar archivos");
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);
  useDataRefresh(load);

  const filesByCategory = useMemo(() => {
    const grouped = Object.fromEntries(CATEGORIES.map((c) => [c.key, []]));
    for (const f of files) {
      if (grouped[f.category]) grouped[f.category].push(f);
      else grouped.OTROS.push(f);
    }
    return grouped;
  }, [files]);

  const doUpload = async (category, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingCat(category);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("category", category);
      form.append("document_template_key", ""); // se puede refinar por UI
      form.append("notes", "");
      const { data } = await api.post(`/students/${studentId}/files`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.deduplicated) {
        toast.info("Archivo idéntico ya existente en OneDrive — no se duplica.");
      } else {
        toast.success(`Subido: ${data?.file?.filename || file.name}`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err?.message || "Error al subir");
    } finally {
      setUploadingCat("");
      event.target.value = ""; // reset input
    }
  };

  const doDownload = async (file) => {
    try {
      const res = await api.get(`/students/${studentId}/files/${file.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename || "archivo";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error al descargar");
    }
  };

  const doArchive = async (file) => {
    try {
      await pin.require("file.archive");
      await api.post(`/students/${studentId}/files/${file.id}/archive`);
      toast.success("Archivo archivado");
      await load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message || "Error";
      if (!/cancel/i.test(m)) toast.error(m);
    }
  };

  const templateKeys = useMemo(
    () => new Set(templates.map((t) => (t.key || "").trim()).filter(Boolean)),
    [templates],
  );

  // V1.33-A · Regla anti-eliminación de documentos de matriz
  const isMatrixDoc = (file) => {
    const k = (file?.document_template_key || "").trim();
    return !!(k && templateKeys.has(k));
  };

  const doValidate = async (file) => {
    if (!window.confirm(`¿Confirmas que el documento "${file.filename}" es correcto?\n\nSe verificará su existencia física en OneDrive antes de marcar como validado.`)) return;
    try {
      const { data } = await api.post(
        `/students/${studentId}/files/${file.id}/validate`,
        { confirmation: true },
      );
      toast.success("✅ Documento validado");
      await load();
      return data;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err?.message || "Error al validar");
    }
  };

  const doReject = async (file, reason, requestNewUpload) => {
    try {
      await api.post(`/students/${studentId}/files/${file.id}/reject`, {
        reason, request_new_upload: !!requestNewUpload,
      });
      toast.success("Documento rechazado");
      setRejecting(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err?.message || "Error al rechazar");
    }
  };

  const doReclassify = async (file, newCategory, newKey, notes) => {
    try {
      await api.post(`/students/${studentId}/files/${file.id}/reclassify`, {
        new_category: newCategory || "",
        new_document_template_key: newKey || "",
        notes: notes || "",
      });
      toast.success("Clasificación actualizada");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err?.message || "Error al reclasificar");
    }
  };

  const doDelete = async (file) => {
    const matrix = isMatrixDoc(file);
    const msg = matrix
      ? `⚠ Este archivo es una INSTANCIA de un documento de la Matriz Documental ("${file.document_template_key}").\n\n` +
        `Se eliminará SOLO este archivo concreto del expediente y de OneDrive.\n` +
        `La definición del tipo permanece en la Matriz Documental.\n\n` +
        `¿Confirmas la eliminación de "${file.filename}"?`
      : `Eliminar definitivamente "${file.filename}" de OneDrive?`;
    if (!window.confirm(msg)) return;
    try {
      await pin.require("file.delete");
      const { data } = await api.delete(`/students/${studentId}/files/${file.id}`);
      if (data?.was_matrix_instance) {
        toast.success("Instancia eliminada · la definición en Matriz se conserva");
      } else {
        toast.success("Archivo eliminado");
      }
      await load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message || "Error";
      if (!/cancel/i.test(m)) toast.error(m);
    }
  };

  const doMigrate = async () => {
    setMigrating(true);
    try {
      await pin.require("corporate.provision");
      const { data } = await api.post(`/students/${studentId}/documents/migrate-to-onedrive`);
      const parts = [];
      if (data.migrated) parts.push(`${data.migrated} migrados`);
      if (data.skipped) parts.push(`${data.skipped} ya migrados`);
      if (data.errors) parts.push(`${data.errors} errores`);
      toast.success(`Migración completada: ${parts.join(" · ") || "sin cambios"}`);
      await load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message || "Error";
      if (!/cancel/i.test(m)) toast.error(m);
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4" data-testid="student-files-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Cloud className="h-4 w-4 text-blue-600" />
          <h3 className="text-sm font-bold">
            Cloud
            <span className="ml-2 text-[10px] font-mono text-neutral-500">V1.37.13</span>
          </h3>
          <span className="text-[11px] text-neutral-500" title="Almacenamiento físico: OneDrive Empresarial">
            {files.length} archivo{files.length === 1 ? "" : "s"} activo{files.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <SyncSignedButton studentId={studentId} onDone={load} />
          <button
            onClick={doMigrate}
            disabled={migrating}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700 disabled:opacity-60"
            title="Sube a OneDrive los documentos base64 heredados del alumno"
            data-testid="migrate-legacy-btn"
          >
            {migrating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
            {migrating ? "Migrando…" : "Migrar legacy"}
          </button>
          <button
            onClick={doFixExtensions}
            disabled={fixing}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-xs font-semibold hover:bg-amber-100 disabled:opacity-60"
            title="Corrige archivos guardados con extensión equivocada (PDF/PNG/HEIC nombrados .jpg)"
            data-testid="fix-extensions-btn"
          >
            {fixing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wrench className="h-3.5 w-3.5" />}
            {fixing ? "Reparando…" : "Reparar extensiones"}
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-1 h-8 px-2 rounded-full border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 text-xs font-semibold"
            data-testid="files-refresh-btn"
          >
            <RefreshCw className={"h-3 w-3 " + (loading ? "animate-spin" : "")} />
            Refrescar
          </button>
        </div>
      </div>

      {loading && files.length === 0 ? (
        <div className="flex items-center gap-2 py-6 text-xs text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando archivos…
        </div>
      ) : (
        <div className="space-y-3">
          {CATEGORIES.map((cat) => {
            const rows = filesByCategory[cat.key] || [];
            const empty = rows.length === 0;
            return (
              <div key={cat.key} className="rounded-lg border border-neutral-200" data-testid={`cat-block-${cat.key}`}>
                <div className="flex items-center justify-between px-3 py-1.5 bg-neutral-50 rounded-t-lg">
                  <div className="flex items-center gap-2 min-w-0">
                    <FolderOpen className="h-3.5 w-3.5 text-neutral-500 shrink-0" />
                    <span className="text-xs font-bold text-neutral-800">{cat.label}</span>
                    <span className="text-[10px] text-neutral-500 truncate">· {cat.hint}</span>
                    {!empty && (
                      <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5">
                        {rows.length}
                      </span>
                    )}
                  </div>
                  <label className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-blue-600 text-white text-[11px] font-semibold hover:bg-blue-700 cursor-pointer" data-testid={`upload-${cat.key}`}>
                    {uploadingCat === cat.key ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Upload className="h-3 w-3" />
                    )}
                    Subir
                    <input
                      ref={(el) => { fileInputs.current[cat.key] = el; }}
                      type="file"
                      className="hidden"
                      disabled={uploadingCat === cat.key}
                      onChange={(e) => doUpload(cat.key, e)}
                    />
                  </label>
                </div>
                {empty ? (
                  <div className="px-3 py-2 text-[11px] text-neutral-400 italic">
                    Sin archivos en esta categoría
                  </div>
                ) : (
                  <div className="divide-y divide-neutral-100">
                    {rows.map((f) => {
                      const Icon = iconForExt(f.extension);
                      const vstat = f.validation_status || "received";
                      const badgeCls =
                        vstat === "validated" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                        : vstat === "rejected" ? "bg-red-100 text-red-800 border-red-200"
                        : "bg-amber-100 text-amber-800 border-amber-200";
                      const badgeLabel =
                        vstat === "validated" ? "✅ Validado"
                        : vstat === "rejected" ? "❌ Rechazado"
                        : "📥 Recibido";
                      const matrix = isMatrixDoc(f);
                      return (
                        <div key={f.id} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-neutral-50" data-testid={`file-row-${f.id}`}>
                          <Icon className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-mono text-[11px] truncate text-neutral-800" title={f.filename}>
                                {f.filename}
                              </span>
                              <span className="text-emerald-700 font-semibold text-[10px]">v{f.version}</span>
                              <span className={"inline-block text-[9px] font-bold rounded-full px-1.5 py-px border " + badgeCls} data-testid={`file-status-${f.id}`}>
                                {badgeLabel}
                              </span>
                              {matrix && (
                                <span className="text-[9px] font-bold text-blue-800 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-px" title="Este documento pertenece a la Matriz Documental">
                                  MATRIZ
                                </span>
                              )}
                              {/* V1.42.6 · Badge CROP clicable → abre el editor */}
                              {f.crop_config && f.crop_config.mode === "manual" && (
                                <button
                                  type="button"
                                  onClick={() => setCropping(f)}
                                  className="text-[9px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-full px-1.5 py-px cursor-pointer transition-colors"
                                  title="Recorte configurado — pulsa para revisarlo/editarlo"
                                  data-testid={`file-crop-badge-${f.id}`}
                                >
                                  CROP
                                </button>
                              )}
                            </div>
                            <div className="text-[10px] text-neutral-500 flex items-center gap-2 flex-wrap">
                              <span>{fmtSize(f.size_bytes)}</span>
                              <span>·</span>
                              <span>{new Date(f.uploaded_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                              <span>·</span>
                              <span className="truncate max-w-[140px]" title={f.uploaded_by}>{f.uploaded_by}</span>
                              {vstat === "rejected" && f.rejection_reason && (
                                <>
                                  <span>·</span>
                                  <span className="text-red-700 italic truncate max-w-[200px]" title={f.rejection_reason}>
                                    {f.rejection_reason}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {/* Ver / abrir en OneDrive */}
                            {f.onedrive_web_url && (
                              <a
                                href={f.onedrive_web_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-blue-600"
                                title="Ver en OneDrive"
                                data-testid={`file-open-${f.id}`}
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                            {/* Descargar */}
                            <button
                              onClick={() => doDownload(f)}
                              className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-blue-600"
                              title="Descargar"
                              data-testid={`file-download-${f.id}`}
                            >
                              <Download className="h-3.5 w-3.5" />
                            </button>
                            {/* V1.33-A · Validar (solo si received) */}
                            {vstat === "received" && (
                              <button
                                onClick={() => doValidate(f)}
                                className="p-1 rounded hover:bg-emerald-100 text-neutral-500 hover:text-emerald-700"
                                title="Validar documento (verifica OneDrive)"
                                data-testid={`file-validate-${f.id}`}
                              >
                                <ClipboardCheck className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {/* V1.42.6 · Ajustar recorte (imágenes y PDFs) */}
                            {((f.content_type || "").startsWith("image/") || (f.content_type || "").toLowerCase().endsWith("pdf")) && (
                              <button
                                onClick={() => setCropping(f)}
                                className="p-1 rounded hover:bg-indigo-100 text-neutral-500 hover:text-indigo-700"
                                title="Ajustar recorte / rotación / filtro"
                                data-testid={`file-crop-${f.id}`}
                              >
                                <Scan className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {/* V1.42.6 · Sustituir el original en OneDrive por la
                                versión recortada. Sólo disponible tras validar. */}
                            {vstat === "validated" && (
                              <button
                                onClick={() => setPreviewingOd(f)}
                                disabled={replacingOd === f.id}
                                className="p-1 rounded hover:bg-indigo-100 text-neutral-500 hover:text-indigo-700 disabled:opacity-50"
                                title="Sustituir el original en OneDrive por la versión recortada (con vista previa)"
                                data-testid={`file-replace-onedrive-${f.id}`}
                              >
                                {replacingOd === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
                              </button>
                            )}
                            {/* V1.33-A · Rechazar */}
                            {vstat !== "rejected" && (
                              <button
                                onClick={() => setRejecting(f)}
                                className="p-1 rounded hover:bg-red-100 text-neutral-500 hover:text-red-700"
                                title="Rechazar"
                                data-testid={`file-reject-${f.id}`}
                              >
                                <XCircle className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {/* V1.33-A · Editar clasificación */}
                            <button
                              onClick={() => setEditing(f)}
                              className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-blue-700"
                              title="Editar clasificación"
                              data-testid={`file-edit-${f.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            {/* Archivar */}
                            <button
                              onClick={() => doArchive(f)}
                              className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-amber-600"
                              title="Archivar"
                              data-testid={`file-archive-${f.id}`}
                            >
                              <Archive className="h-3.5 w-3.5" />
                            </button>
                            {/* V1.33-A.1 · Eliminar SIEMPRE visible (requiere PIN).
                                Si es instancia de Matriz, el confirm y toast explican
                                que se elimina la instancia, no la definición. */}
                            <button
                              onClick={() => doDelete(f)}
                              className={
                                "p-1 rounded hover:bg-neutral-100 " +
                                (matrix
                                  ? "text-neutral-400 hover:text-orange-600"
                                  : "text-neutral-500 hover:text-red-600")
                              }
                              title={
                                matrix
                                  ? "Eliminar instancia (la definición en Matriz se conserva) — requiere PIN"
                                  : "Eliminar de OneDrive (requiere PIN)"
                              }
                              data-testid={`file-delete-${f.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer hint */}
      <div className="mt-3 flex items-start gap-2 text-[11px] text-neutral-500 rounded-lg bg-neutral-50 p-2">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
        <span>
          Los archivos se almacenan físicamente en <b>OneDrive Empresarial</b> (repositorio corporativo).
          Cada subida crea una nueva versión — la anterior queda archivada lógicamente
          y accesible mediante auditoría.
        </span>
      </div>

      {/* V1.33-A · Modal rechazar */}
      {rejecting && (
        <RejectFileModal
          file={rejecting}
          onClose={() => setRejecting(null)}
          onConfirm={(reason, req) => doReject(rejecting, reason, req)}
        />
      )}

      {/* V1.33-A · Modal editar clasificación */}
      {editing && (
        <ReclassifyFileModal
          file={editing}
          templates={templates}
          onClose={() => setEditing(null)}
          onConfirm={(cat, key, notes) => doReclassify(editing, cat, key, notes)}
        />
      )}

      {/* V1.42.6 · Diálogo de recorte manual (reutiliza ManualCropDialog) */}
      {cropping && (
        <ManualCropDialog
          open={!!cropping}
          onClose={() => setCropping(null)}
          studentId={studentId}
          fileId={cropping.id}
          filename={cropping.filename}
          initialConfig={cropping.crop_config || null}
          onSaved={() => { load(); }}
        />
      )}

      {/* V1.42.8 · Vista previa antes de sustituir en OneDrive */}
      {previewingOd && (
        <CropPreviewDialog
          open={!!previewingOd}
          onClose={() => setPreviewingOd(null)}
          title={`Vista previa · ${previewingOd.filename}`}
          previewUrl={`/students/${studentId}/files/${previewingOd.id}/preview-crop`}
          hasCrop={!!(previewingOd.crop_config && previewingOd.crop_config.mode === "manual")}
          onConfirm={async () => {
            setReplacingOd(previewingOd.id);
            try {
              await pin.require("file.replace_original");
              const { data } = await api.post(`/students/${studentId}/files/${previewingOd.id}/apply-crop-to-onedrive`);
              toast.success(data?.applied_crop
                ? "OneDrive actualizado con la versión recortada"
                : "OneDrive re-subido (sin recorte)");
              await load();
            } finally { setReplacingOd(""); }
          }}
        />
      )}
    </div>
  );
}

// ─── V1.33-A · Modales auxiliares ─────────────────────────────────────────

function RejectFileModal({ file, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [reqUpload, setReqUpload] = useState(false);
  const PRESETS = [
    "Documento ilegible",
    "Documento incompleto",
    "Psicotécnico caducado",
    "DNI incorrecto",
    "Formato no admitido",
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reject-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-3 shadow-xl">
        <div className="flex items-center gap-2">
          <XCircle className="h-5 w-5 text-red-600" />
          <h3 className="font-bold">Rechazar documento</h3>
        </div>
        <p className="text-xs text-neutral-500 truncate" title={file.filename}>
          {file.filename}
        </p>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-neutral-700">Motivo (obligatorio)</label>
          <div className="flex flex-wrap gap-1 mb-2">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setReason(p)}
                className="text-[10px] px-2 py-0.5 rounded-full border border-neutral-200 bg-neutral-50 hover:bg-neutral-100"
                data-testid={`reject-preset-${p.slice(0, 8)}`}
              >
                {p}
              </button>
            ))}
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full text-sm rounded-lg border border-neutral-200 p-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            placeholder="Explica el motivo..."
            data-testid="reject-reason-input"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-neutral-700">
          <input
            type="checkbox"
            checked={reqUpload}
            onChange={(e) => setReqUpload(e.target.checked)}
            data-testid="reject-request-upload"
          />
          Solicitar nuevo envío al alumno (notificación automática)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-full text-xs border border-neutral-300 hover:bg-neutral-50"
            data-testid="reject-cancel"
          >
            Cancelar
          </button>
          <button
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason.trim(), reqUpload)}
            className="px-3 py-1.5 rounded-full text-xs bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-50"
            data-testid="reject-confirm"
          >
            Rechazar
          </button>
        </div>
      </div>
    </div>
  );
}

function ReclassifyFileModal({ file, templates, onClose, onConfirm }) {
  const [cat, setCat] = useState(file.category || "OTROS");
  const [key, setKey] = useState(file.document_template_key || "");
  const [notes, setNotes] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reclassify-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-3 shadow-xl">
        <div className="flex items-center gap-2">
          <Pencil className="h-5 w-5 text-blue-600" />
          <h3 className="font-bold">Editar clasificación</h3>
        </div>
        <p className="text-xs text-neutral-500 truncate" title={file.filename}>{file.filename}</p>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-neutral-700">Categoría destino</label>
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            className="w-full text-sm rounded-lg border border-neutral-200 p-2"
            data-testid="reclassify-category"
          >
            {CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-neutral-700">Tipo (matriz documental, opcional)</label>
          <select
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="w-full text-sm rounded-lg border border-neutral-200 p-2"
            data-testid="reclassify-template"
          >
            <option value="">— sin clasificar —</option>
            {templates.map((t) => (
              <option key={t.key} value={t.key}>{t.name || t.key}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-neutral-700">Notas (opcional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full text-sm rounded-lg border border-neutral-200 p-2"
            data-testid="reclassify-notes"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-full text-xs border border-neutral-300 hover:bg-neutral-50"
            data-testid="reclassify-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(cat, key, notes)}
            className="px-3 py-1.5 rounded-full text-xs bg-blue-600 text-white font-bold hover:bg-blue-700"
            data-testid="reclassify-confirm"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
